// §4.1 conversations inbox + §4.2 unclaimed work (Module 17, slice P3-B
// part 2) — three panes on one screen.
//
//   LEFT   the thread list: six filter tabs (Mine / Unassigned / All /
//          Snoozed / Closed / Unclaimed — §4.2 lives here as the `unclaimed`
//          tab), a search on customer or contact name, plus the chat-id or
//          phone tail for a reader who holds the address grant (#272), rows
//          with provider word, preview, unread pill, relative time and the
//          WA session-countdown chip when < 4 h remain.
//   CENTER the thread: bubbles with status WORDS for ticks (the register has
//          no icon font), `status_detail` on hover and Retry on
//          `failed_retryable`, template badges, inline media off the signed
//          URLs, date separators, upward cursor pagination; the composer —
//          free text, attach (upload-url → PUT → media_s3_keys), template
//          picker — swaps to picker-only with the "Session closed" chip when
//          the WA 24 h window is shut (§4.1; 409 WA_WINDOW_CLOSED does the
//          same swap when the window closes mid-typing, §9.3).
//   RIGHT  context: customer card + linked-orders block, identity block
//          (masked address per §7), Assign / Snooze / Close, the §4.2
//          attach-to-customer claim and Mark spam. On phone widths the panes
//          collapse to a list→thread stack and this panel becomes a sheet.
//
// ROLES (§7): the read-only trio (planner/finance/qc) holds
// `conversation.read` only — for them the composer, the action controls, the
// claim panel and Retry DO NOT RENDER (redaction as absence, the sales
// permissions.ts fail-closed mirror); addresses arrive masked from the
// serializer, which is §7's stated form for addresses.
//
// ASSIGN without a member directory: `GET /v1/members` rides
// `identity.view_members` (ga/fa only), so the admin sees a name dropdown and
// a sales seat gets "Assign to me" — the `"me"` literal arm the P3-0
// adjudication added to the PATCH beside uuid|null, resolved server-side, so
// no self-id feed and no directory read exist to go dead (D-088).
//
// EMAIL (P3-E2 PR E2-2e, D-327). An email thread is inbound only: each
// message shows its subject, its mark ("Sender domain verified" or "Sender
// not verified"), who released it, its stored, held and omitted files, and
// the quoted part folded under "Earlier message" for a reader the server
// serves it to. The composer is replaced by one sentence. An unclaimed email
// thread opens on the triage card (§4.2, spec 09 §4.11), and holders of
// `messaging.conversation.manage` get the Unverified mail tab beside
// Unclaimed, where held mail is released or discarded. A sender is an
// address, painted as the server served it: in clear with its display name
// for a holder of `messaging.address.read`, masked and with no name for
// every other reader (D-327 rulings 13 and 20).
//
// TAB + SEARCH + SELECTED THREAD live in the URL (validateInboxSearch): the
// inbox is a SHARED worklist (~10 managers), so "look at this thread" must
// paste as an address and a reload must not lose the tab (the module-04
// work-order-list rule).

import { normalizeLocale, SOURCE_LOCALE, SUPPORTED_LOCALES } from "@assessly/contracts/locales";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, ReactNode } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { RefLabel } from "../../../../components/ref-label";
import { Select } from "../../../../components/select";
import { StatusChip } from "../../../../components/status-chip";
import { NavTabs, type TabDef } from "../../../../components/tabs";
import {
  CardSkeleton,
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { requestIdOf, RequestError } from "../../../../lib/request";
import { formatBusinessDate, formatTimestamp, timestampDayIso } from "../../../../lib/datetime";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { MutationFailureNote } from "../mutation-failure-note";
import { isMessagingReadRefused, useMessagingPermissions } from "../hooks/permissions";
import {
  foldThreadPages,
  useConversation,
  useConversations,
  useCustomerContacts,
  useCustomerSearch,
  useDiscardHeldMail,
  useEmailQuarantine,
  useHeldPartLink,
  useMarkRead,
  useMemberIndex,
  useMessagingOnline,
  useMessagingTemplates,
  usePatchConversation,
  useReleaseHeldMail,
  useRetryMessage,
  useRevokeIdentity,
  useSendReply,
  useThreadMessagePages,
  useUploadAttachment,
  type Conversation,
  type ConversationsQuery,
  type EmailHeldPart,
  type EmailOmittedPart,
  type EmailQuarantineItem,
  type MessageTemplate,
  type ThreadMessage,
} from "../hooks/messaging-queries";
import {
  displayAddress,
  isInlineImageType,
  refusedSendText,
  relativeAge,
  snoozeUntil,
  telegramDisplayText,
  timeRemaining,
  waChipVisible,
} from "../lib/format";
import { MEDIA_MAX_FILES, attachRefusal, captionOverflow } from "../lib/attachments";
import { TemplatePreview } from "../lib/template-preview";
import {
  pageRangeLabel,
  useCursorStack,
  useLoadedPager,
  useStackWindow,
} from "../lib/cursor-pages";
import { MsgPager } from "../components/msg-pager";
import { OfferAgentStrip } from "../components/offer-agent-strip";
// §4.1's context pane reaches into module 09 for two things — the customer's
// recent ORDERS and the door into the quote composer. Both read the SALES
// mirror, not this module's: a `sales.*` code has no cell in
// MESSAGING_ROLE_GRANTS (its CI mirror is checked against the messaging slice
// of the contract table), so copying one here would be a second, drifting
// transcription of somebody else's row. The quality screens read
// `useInventoryPermissions` for the same reason.
import {
  canWriteOpportunities,
  isSalesReadRefused,
  useSalesPermissions,
} from "../../sales/hooks/permissions";
import { useOrders } from "../../sales/hooks/quote-queries";
import { orderStatusVariant } from "../../sales/screens/orders";
// The attachments panel's three pure helpers: a file type's short name, a
// size in the reader's locale, and the anchor click that starts a download
// of a minted link without leaving the page.
import {
  formatSize,
  startDownload,
  typeLabel,
} from "../../sales/components/attachments/attachment-model";

const INBOX_TABS = [
  "mine",
  "unassigned",
  "all",
  "snoozed",
  "closed",
  "unclaimed",
  // §4.2's Unverified mail (D-327 rulings 7 and 18): not a conversation
  // filter, so it reads the quarantine list in place of the thread list.
  "held",
] as const;
type InboxTab = (typeof INBOX_TABS)[number];

export interface InboxSearch {
  /** The selected conversation — the pasteable address. */
  c?: string;
  tab?: InboxTab;
  q?: string;
}

export function validateInboxSearch(search: Record<string, unknown>): InboxSearch {
  const out: InboxSearch = {};
  if (typeof search["c"] === "string" && search["c"].length > 0) out.c = search["c"];
  if (
    typeof search["tab"] === "string" &&
    (INBOX_TABS as readonly string[]).includes(search["tab"])
  )
    out.tab = search["tab"] as InboxTab;
  if (typeof search["q"] === "string" && search["q"].length > 0) out.q = search["q"];
  return out;
}

const TAB_LABEL: Readonly<Record<InboxTab, string>> = {
  mine: "messaging.inbox.tabMine",
  unassigned: "messaging.inbox.tabUnassigned",
  all: "messaging.inbox.tabAll",
  snoozed: "messaging.inbox.tabSnoozed",
  closed: "messaging.inbox.tabClosed",
  unclaimed: "messaging.inbox.tabUnclaimed",
  held: "messaging.inbox.tabHeld",
};

/** A chat provider: the threads a person can write in. An email thread is
 * inbound only (D-327 ruling 9), so nothing that sends is ever handed one. */
type ChatProvider = Exclude<Conversation["provider"], "email">;

/**
 * An email sender as this reader was served it (D-327 rulings 13 and 20): the
 * From display name with the address for a holder of
 * `messaging.address.read`, and for every other reader the masked address
 * alone, because the server serves that reader no display name. Pure.
 */
export function emailSenderLabel(
  t: ReturnType<typeof useTranslation>["t"],
  identity: Pick<Conversation["identity"], "address" | "provider_profile_name">,
): string {
  const name = identity.provider_profile_name;
  return name !== null && name.trim().length > 0
    ? t("messaging.email.senderNamed", { name, address: identity.address })
    : identity.address;
}

/** D-327 ruling 7: a display name holding `@` can pose as another address.
 * Only a reader served the name can be misled by it, and that reader is
 * shown the address that sent the mail. Pure. */
export function displayNameHoldsAddress(
  identity: Pick<Conversation["identity"], "provider_profile_name">,
): boolean {
  return identity.provider_profile_name?.includes("@") ?? false;
}

/** The §5 upload-url mime allowlist, as the file input's own filter. */
const ATTACH_ACCEPT = "image/*,application/pdf,.doc,.docx,.xls,.xlsx,audio/*";

/** Tab → the §5 filter set (D-142: declared params only). `assigned` is the
 * P3-0-adjudicated pair of literals ("me"/"none"), resolved server-side. */
function tabQuery(tab: InboxTab, q: string): ConversationsQuery {
  const base: ConversationsQuery =
    tab === "mine"
      ? { assigned: "me", status: "open" }
      : tab === "unassigned"
        ? { assigned: "none", status: "open" }
        : tab === "snoozed"
          ? { status: "snoozed" }
          : tab === "closed"
            ? { status: "closed" }
            : tab === "unclaimed"
              ? { unclaimed: "true" }
              : // `all`, and `held`, which reads no conversation list.
                {};
  return q.length > 0 ? { ...base, q } : base;
}

/** The server's inbox ordering key, re-applied after page folds so a fresh
 * page-1 row never renders below older accumulated rows (§5: sorted
 * `last_inbound_at DESC`, creation time for silent threads). */
function inboxSortKey(row: Conversation): number {
  const key = row.last_inbound_at ?? row.created_at;
  const ms = Date.parse(key);
  return Number.isNaN(ms) ? 0 : ms;
}

export function MessagingInboxScreen() {
  const { t, i18n } = useTranslation();
  const perms = useMessagingPermissions();
  const navigate = useNavigate();
  const search: InboxSearch = useSearch({ from: "/messaging/inbox" });
  const selectedId = search.c;
  const canWork = perms.canWorkThreads;
  // Unverified mail is for holders of `messaging.conversation.manage` alone.
  // A settled mirror that denies it reads a `held` address as All, the tab
  // that reader has; a mirror still in flight keeps it and sends neither
  // read until it settles (the gate below waits on the mirror anyway).
  const tab: InboxTab =
    search.tab === "held" && perms.isResolved && !canWork ? "all" : (search.tab ?? "all");
  const heldTab = tab === "held";
  // The hint offers the chat-id or phone tail only to a reader the server
  // matches it for: `q` leaves the address out of the search for anyone
  // without `messaging.address.read` (spec 17 §7, #272). Read from the
  // address grant itself, never from canWork: every system role happens to
  // hold both or neither, but the server decides on the address grant
  // alone. A custom role, a feed still in flight or a failed one reads as no
  // grant here (the mirror is fail-closed), so such a reader is offered the
  // names, which match for everyone, and never a tail that may match nothing.
  const searchHint = perms.canReadAddress
    ? t("messaging.inbox.searchHintWithTail")
    : t("messaging.inbox.searchHintNamesOnly");

  // Search box: local echo of the URL, debounced back into it (replace, so
  // typing does not spray history entries). Navigation passes the WHOLE next
  // search object (the work-orders-list idiom) — a reducer's `prev` types as
  // the union of every route's search under exactOptionalPropertyTypes.
  const [qInput, setQInput] = useState(search.q ?? "");
  const qDebounced = useDebouncedValue(qInput.trim(), 300);
  useEffect(() => {
    if (qDebounced === (search.q ?? "")) return;
    const next: InboxSearch = { ...search };
    if (qDebounced.length > 0) next.q = qDebounced;
    else delete next.q;
    void navigate({ to: "/messaging/inbox", replace: true, search: next });
  }, [qDebounced, search, navigate]);

  // ONE PAGE AT A TIME (design-system §3.17 rev-7, spec 17 §4.1). The list
  // shows the page its cursor reads, 25 threads, and Newer and Older move
  // along the cursors walked; a new tab or search starts again at page 1.
  // Until the P3-UX leftovers this folded every page read into one growing
  // list, which past 50 rows is what rev-7 forbids. The open thread lives in
  // the URL (`?c=`), so turning the page never closes it.
  const query = useMemo(() => tabQuery(tab, qDebounced), [tab, qDebounced]);
  const resetKey = `${tab}|${qDebounced}`;
  const pages = useCursorStack(resetKey);
  const allowed = perms.canOpenInbox;
  const refused = isMessagingReadRefused(perms, allowed);
  const readsEnabled = allowed && !refused;
  const listEnabled = readsEnabled && !heldTab;
  const list = useConversations(query, pages.cursor, listEnabled);
  const quarantineEnabled = readsEnabled && heldTab && canWork;
  const quarantine = useEmailQuarantine(quarantineEnabled);
  const listItems = list.data?.items;
  const rows: readonly Conversation[] = listItems ?? [];
  // While the next page is read the previous one stays on screen (the
  // query's placeholder), and so does its range: the window is taken from
  // the stack that read the rows shown, not from the stack just pushed. A
  // page past the first that empties (its last thread released or moved to
  // another tab) steps back rather than saying the inbox is empty.
  const listWindow = useStackWindow(pages, {
    items: rows,
    nextCursor: list.data?.next_cursor,
    turning: list.isPlaceholderData,
    settled: list.data !== undefined && !list.isFetching,
  });
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => inboxSortKey(b) - inboxSortKey(a)),
    [rows],
  );

  // Deep-linked or clicked-open thread: zero the unread counter ONCE per
  // conversation (§5 read POST) — manage holders only; the read-only trio
  // does not hold the route and firing it would be the D-088 dead call.
  const markRead = useMarkRead();
  const lastMarked = useRef<string | undefined>(undefined);
  const head = useConversation(selectedId ?? "", readsEnabled);
  const headRow = head.data;
  // The deep-linked thread's message pages, observed at screen level so a
  // `?c=` entry's settled shape includes its bubbles: the same infinite query
  // ThreadPane observes (one key, one fetch, one cache entry), so the pane
  // mounts on a warm cache, already settled. Enabled exactly as the head read
  // is (the hook itself refuses an empty id).
  const firstPage = useThreadMessagePages(selectedId ?? "", readsEnabled);
  useEffect(() => {
    if (!canWork || headRow === undefined) return;
    if (headRow.unread_count === 0 || lastMarked.current === headRow.id) return;
    lastMarked.current = headRow.id;
    markRead.mutate(headRow.id);
  }, [canWork, headRow, markRead]);

  // Phone widths: list → thread stack; the context panel becomes a sheet.
  const [contextOpen, setContextOpen] = useState(false);
  useEffect(() => {
    setContextOpen(false);
  }, [selectedId]);

  /* ---- the §5 render-once gate --------------------------------------------
   * `msg-inbox-screen` is a walkthrough CHROME selector (assertRenderedOnce,
   * M17-S02): its first paint must be its settled shape. Painting the testid
   * over an empty list let the folded thread rows land moments later (the
   * 2026-09-01 staging red: root 354→939 px — CLS ≠ 0 on load), so the screen
   * holds a reserving skeleton (home-screen's idiom: distinct testid, `key`
   * remount at reveal) until every read the first paint derives from has
   * settled: the permission mirror (composer/actions/claim presence), the
   * conversations page one LANDED in `rows` — the fold is an effect, so data
   * paints one tick after the fetch — and, on a `?c=` deep link, the thread
   * head plus its mirrored first message page. A read the caller cannot make
   * is excluded: a disabled query never fetches, and waiting on it would hold
   * the skeleton forever (the home-screen rule). Error and empty are settled
   * shapes — they reveal and state themselves — and a paused read (offline)
   * states itself too: never an eternal skeleton.
   *
   * LATCHED — the gate is for the FIRST paint only. Tab/search/selection
   * changes re-key the reads (pending again), but those follow user input:
   * their loading renders INSIDE the settled chrome (the work-order-detail
   * rule), and the screen never re-skeletons underneath the user. */
  const listSettled = list.isError || listItems !== undefined;
  // Unverified mail's first page is a settled shape fetched or failed, as the
  // list's is; it is folded in render, so it needs no landing term.
  const quarantineSettled = quarantine.isError || quarantine.data !== undefined;
  const headNeeded = readsEnabled && selectedId !== undefined;
  const settled =
    perms.isResolved &&
    (!listEnabled || listSettled) &&
    (!quarantineEnabled || quarantineSettled) &&
    (!headNeeded || (!head.isPending && !firstPage.isPending));
  const revealed = useRef(false);
  if (settled) revealed.current = true;

  if (refused) {
    return (
      <ForbiddenState title={t("messaging.access.title")} body={t("messaging.access.inbox")} />
    );
  }

  if (!revealed.current) {
    if (
      (list.isPending && list.fetchStatus === "paused") ||
      (quarantine.isPending && quarantine.fetchStatus === "paused") ||
      (head.isPending && head.fetchStatus === "paused") ||
      (firstPage.isPending && firstPage.fetchStatus === "paused")
    ) {
      return (
        <OfflineState title={t("messaging.offline.title")} body={t("messaging.offline.body")} />
      );
    }
    // Reserving skeleton: the three-pane grid holds the final geometry (the
    // same classes the settled screen wears), each pane a shimmer. Distinct
    // `key` forces a clean remount at reveal — nothing painted here ever
    // moves (§3.9).
    return (
      <div
        className="msg-inbox"
        data-view={selectedId !== undefined ? "thread" : "list"}
        data-testid="msg-inbox-skeleton"
        key="msg-inbox-skeleton"
      >
        <section className="msg-pane msg-pane-list" aria-label={t("messaging.inbox.title")}>
          <TableSkeleton rows={6} cols={2} label={t("messaging.common.loading")} />
        </section>
        <section className="msg-pane msg-pane-thread" aria-label={t("messaging.nav.inbox")}>
          <CardSkeleton lines={6} label={t("messaging.common.loading")} />
        </section>
        <aside className="msg-context" aria-label={t("messaging.context.aria")} />
      </div>
    );
  }

  // Unverified mail is ABSENT for a reader who cannot release or discard,
  // never a tab that opens onto a refusal (D-088).
  const tabs: TabDef[] = INBOX_TABS.filter((id) => id !== "held" || canWork).map((id) => ({
    id,
    label: t(TAB_LABEL[id]),
    "data-testid": `msg-inbox-tab-${id}`,
  }));

  const selectThread = (id: string | undefined) => {
    const next: InboxSearch = { ...search };
    if (id !== undefined) next.c = id;
    else delete next.c;
    void navigate({ to: "/messaging/inbox", search: next });
  };

  return (
    <div
      className={contextOpen ? "msg-inbox msg-context-open" : "msg-inbox"}
      data-view={selectedId !== undefined ? "thread" : "list"}
      data-testid="msg-inbox-screen"
      key="msg-inbox-ready"
    >
      <section className="msg-pane msg-pane-list" aria-label={t("messaging.inbox.title")}>
        <NavTabs
          tabs={tabs}
          active={tab}
          label={t("messaging.inbox.tabsAria")}
          data-testid="msg-inbox-tabs"
          onSelect={(id) => {
            void navigate({
              to: "/messaging/inbox",
              search: { ...search, tab: id as InboxTab },
            });
          }}
        />
        {heldTab ? (
          <UnverifiedMailList query={quarantine} onOpenThread={selectThread} />
        ) : (
          <>
            <Field label={t("messaging.inbox.search")} wide data-testid="msg-search-field">
              <input
                type="search"
                value={qInput}
                placeholder={searchHint}
                data-testid="msg-search"
                onChange={(e) => {
                  setQInput(e.target.value);
                }}
              />
            </Field>

            <ThreadList
              t={t}
              locale={i18n.language}
              listState={list}
              rows={sortedRows}
              tab={tab}
              hasFilter={qDebounced.length > 0}
              selectedId={selectedId}
              pager={
                <MsgPager
                  range={pageRangeLabel(t, i18n.language, listWindow)}
                  hasPrev={listWindow.hasPrev}
                  hasNext={listWindow.hasNext}
                  busy={list.isPlaceholderData}
                  busyLabel={t("messaging.common.loading")}
                  prevLabel={t("messaging.common.pageNewer")}
                  nextLabel={t("messaging.common.pageOlder")}
                  onPrev={pages.prev}
                  onNext={() => {
                    const next = list.data?.next_cursor;
                    if (next !== null && next !== undefined) pages.next(next, rows.length);
                  }}
                  testId="msg-thread-pager"
                />
              }
              onPick={selectThread}
              onEmptyCta={() => {
                void navigate({ to: "/sales/customers" });
              }}
            />
          </>
        )}
      </section>

      <section className="msg-pane msg-pane-thread" aria-label={t("messaging.nav.inbox")}>
        {selectedId === undefined ? (
          // A calm empty pane, not a sentence floating in a wide blank: the
          // list widens while no thread is open (messaging.css), and opening
          // one is the reader's choice, because opening marks it read.
          <div className="msg-thread-none" data-testid="msg-thread-none">
            <EmptyState
              title={t("messaging.thread.pickOne")}
              body={t("messaging.thread.pickOneBody")}
            />
          </div>
        ) : (
          <ThreadPane
            key={selectedId}
            conversationId={selectedId}
            head={head}
            canWork={canWork}
            locale={i18n.language}
            onBack={() => {
              selectThread(undefined);
            }}
            onOpenContext={() => {
              setContextOpen(true);
            }}
          />
        )}
      </section>

      {selectedId !== undefined && headRow !== undefined ? (
        <ContextPanel
          head={headRow}
          canWork={canWork}
          isAdmin={perms.isAdmin}
          onCloseSheet={() => {
            setContextOpen(false);
          }}
        />
      ) : (
        <aside className="msg-context" aria-label={t("messaging.context.aria")} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// left pane — the thread list
// ---------------------------------------------------------------------------

function ThreadList({
  t,
  locale,
  listState,
  rows,
  tab,
  hasFilter,
  selectedId,
  pager,
  onPick,
  onEmptyCta,
}: {
  t: ReturnType<typeof useTranslation>["t"];
  locale: string;
  listState: ReturnType<typeof useConversations>;
  rows: readonly Conversation[];
  tab: InboxTab;
  hasFilter: boolean;
  selectedId: string | undefined;
  /** The page's pager, rendered under the list; nothing on a single page. */
  pager: ReactNode;
  onPick: (id: string) => void;
  onEmptyCta: () => void;
}) {
  if (listState.isPending && rows.length === 0) {
    if (listState.fetchStatus === "paused") {
      return (
        <OfflineState title={t("messaging.offline.title")} body={t("messaging.offline.body")} />
      );
    }
    return <TableSkeleton rows={6} cols={2} label={t("messaging.common.loading")} />;
  }
  if (listState.isError && rows.length === 0) {
    return (
      <ErrorState
        title={t("messaging.inbox.error")}
        retryLabel={t("messaging.common.retry")}
        requestId={requestIdOf(listState.error)}
        requestIdLabel={t("messaging.common.requestId")}
        onRetry={() => {
          void listState.refetch();
        }}
      />
    );
  }
  if (rows.length === 0) {
    // §4.1/§4.2 name the two special empties; a filtered miss and the other
    // tabs get the honest generic line.
    if (tab === "unassigned" && !hasFilter) {
      return (
        <p className="msg-hint" data-testid="msg-empty-unassigned">
          {t("messaging.inbox.emptyUnassigned")}
        </p>
      );
    }
    if (tab === "unclaimed" && !hasFilter) {
      return (
        <p className="msg-hint" data-testid="msg-empty-unclaimed">
          {t("messaging.inbox.emptyUnclaimed")}
        </p>
      );
    }
    if (tab === "all" && !hasFilter) {
      return (
        <EmptyState
          title={t("messaging.inbox.emptyTitle")}
          body={t("messaging.inbox.emptyBody")}
          ctaLabel={t("messaging.inbox.emptyCta")}
          onCta={onEmptyCta}
        />
      );
    }
    return (
      <p className="msg-hint" data-testid="msg-empty-filtered">
        {t("messaging.inbox.emptyFiltered")}
      </p>
    );
  }

  return (
    <>
      <ul
        className="msg-thread-list"
        aria-label={t("messaging.inbox.listAria")}
        data-testid="msg-thread-list"
      >
        {rows.map((row) => (
          <li key={row.id}>
            <ThreadRow
              t={t}
              locale={locale}
              row={row}
              selected={row.id === selectedId}
              onPick={onPick}
            />
          </li>
        ))}
      </ul>
      {pager}
    </>
  );
}

function ThreadRow({
  t,
  locale,
  row,
  selected,
  onPick,
}: {
  t: ReturnType<typeof useTranslation>["t"];
  locale: string;
  row: Conversation;
  selected: boolean;
  onPick: (id: string) => void;
}) {
  const email = row.provider === "email";
  // An email sender is an address (§7): the row names it as served, and on a
  // claimed thread it rides under the customer's name.
  const sender = email ? emailSenderLabel(t, row.identity) : undefined;
  const name =
    row.customer?.display_name ??
    sender ??
    row.identity.provider_profile_name ??
    t("messaging.inbox.unknownName");
  const age = relativeAge(t, row.last_inbound_at ?? row.last_outbound_at ?? row.created_at);
  const waChip = row.provider === "whatsapp" && waChipVisible(row.wa_window);
  return (
    <button
      type="button"
      className="msg-thread-row"
      aria-current={selected ? "true" : undefined}
      data-testid="msg-thread-row"
      data-conversation-id={row.id}
      onClick={() => {
        onPick(row.id);
      }}
    >
      <span className="msg-thread-row-top">
        <span className="msg-provider">{t(`messaging.provider.${row.provider}`)}</span>
        <span className="msg-thread-name">{name}</span>
        {age !== undefined ? <span className="msg-thread-time num">{age}</span> : null}
      </span>
      {sender !== undefined && row.customer !== null ? (
        <span className="msg-thread-preview" data-testid="msg-thread-sender">
          {sender}
        </span>
      ) : null}
      <span className="msg-thread-preview">
        {row.last_message_preview ?? t("messaging.inbox.noPreview")}
      </span>
      <span className="msg-thread-chips">
        {row.unread_count > 0 ? (
          <span
            className="msg-unread num"
            aria-label={t("messaging.inbox.unread", { count: row.unread_count })}
            data-testid="msg-unread"
          >
            {row.unread_count}
          </span>
        ) : null}
        {waChip ? (
          <StatusChip variant="warn" data-testid="msg-wa-window">
            {t("messaging.inbox.waWindow", {
              time: timeRemaining(t, row.wa_window.closes_at) ?? "",
            })}
          </StatusChip>
        ) : null}
        {row.status === "snoozed" && row.snoozed_until !== null ? (
          <StatusChip variant="neutral" dot={false}>
            {t("messaging.context.snoozedUntil", {
              date: formatTimestamp(row.snoozed_until, locale),
            })}
          </StatusChip>
        ) : null}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// center pane — the thread + composer
// ---------------------------------------------------------------------------

function ThreadPane({
  conversationId,
  head,
  canWork,
  locale,
  onBack,
  onOpenContext,
}: {
  conversationId: string;
  head: ReturnType<typeof useConversation>;
  canWork: boolean;
  locale: string;
  onBack: () => void;
  onOpenContext: () => void;
}) {
  const { t } = useTranslation();
  // Every loaded page is ONE infinite query (D-322 ruling 7). Its poll
  // re-reads every loaded page, so a bubble on an older page gets its fresh
  // media link on the same pace as page one. The bubbles are DERIVED from the
  // query, never copied into state: a copy is what a refetch can leave
  // behind, and it did, once "Show earlier" moved a one-page observer off
  // page one. Page one is normally already in cache when this pane mounts
  // (the screen gate observes the same query), so render zero carries its
  // bubbles, not a one-frame skeleton.
  const page = useThreadMessagePages(conversationId);
  const messages = useMemo(() => foldThreadPages(page.data?.pages), [page.data]);
  const ordered = useMemo(
    () =>
      [...messages].sort((a, b) => {
        const am = Date.parse(a.created_at);
        const bm = Date.parse(b.created_at);
        return am - bm;
      }),
    [messages],
  );

  // Auto-scroll to the newest bubble — except when the user just asked for
  // OLDER pages, whose whole point is reading upward without the pane
  // snapping back down.
  const bubblesRef = useRef<HTMLDivElement | null>(null);
  const skipScroll = useRef(false);
  const newestId = ordered.length > 0 ? ordered[ordered.length - 1]?.id : undefined;
  useEffect(() => {
    if (skipScroll.current) {
      skipScroll.current = false;
      return;
    }
    const el = bubblesRef.current;
    if (el !== null) el.scrollTop = el.scrollHeight;
  }, [newestId]);

  const retry = useRetryMessage();
  const headRow = head.data;

  /* `msg-thread-screen` is itself a render-once chrome selector (M17-S12), so
   * ITS first paint must be its settled shape too: ONE skeleton until the
   * head AND the first message page have landed. The bubbles derive from the
   * query in the same render, so a warm-cache mount lands on render zero.
   * Latched per mount (the pane re-keys per conversation), so Show earlier
   * loads while the bubbles already shown keep the settled pane's shape,
   * never by re-skeletoning it. A paused read (offline) states itself, the
   * area idiom, rather than skeletoning forever. */
  const pageLanded = page.isError || page.data !== undefined;
  const settledOnce = useRef(false);
  if (!head.isPending && pageLanded) settledOnce.current = true;

  if (head.isPending || (!head.isError && !settledOnce.current)) {
    if (
      (head.isPending && head.fetchStatus === "paused") ||
      (page.isPending && page.fetchStatus === "paused")
    ) {
      return (
        <OfflineState title={t("messaging.offline.title")} body={t("messaging.offline.body")} />
      );
    }
    return <CardSkeleton lines={6} label={t("messaging.common.loading")} />;
  }
  if (head.isError || headRow === undefined) {
    return (
      <ErrorState
        title={t("messaging.thread.error")}
        retryLabel={t("messaging.common.retry")}
        requestId={requestIdOf(head.error)}
        requestIdLabel={t("messaging.common.requestId")}
        onRetry={() => {
          void head.refetch();
        }}
      />
    );
  }

  const email = headRow.provider === "email";
  const name =
    headRow.customer?.display_name ??
    (email ? emailSenderLabel(t, headRow.identity) : undefined) ??
    headRow.identity.provider_profile_name ??
    t("messaging.inbox.unknownName");
  const unclaimedEmail = email && headRow.kind === "customer" && headRow.customer_id === null;

  return (
    <div className="msg-thread" data-testid="msg-thread-screen">
      <div className="msg-toolbar">
        <Button size="sm" className="msg-back" data-testid="msg-back" onClick={onBack}>
          {t("messaging.thread.backToList")}
        </Button>
        <span className="msg-thread-name">{name}</span>
        <span className="msg-provider">{t(`messaging.provider.${headRow.provider}`)}</span>
        <Button
          size="sm"
          className="msg-sheet-toggle"
          data-testid="msg-context-toggle"
          onClick={onOpenContext}
        >
          {t("messaging.context.open")}
        </Button>
      </div>

      {unclaimedEmail ? <EmailTriageCard head={headRow} ordered={ordered} /> : null}

      {page.isError && messages.length === 0 ? (
        <ErrorState
          title={t("messaging.thread.messagesError")}
          retryLabel={t("messaging.common.retry")}
          requestId={requestIdOf(page.error)}
          requestIdLabel={t("messaging.common.requestId")}
          onRetry={() => {
            void page.refetch();
          }}
        />
      ) : (
        <div className="msg-bubbles" ref={bubblesRef} data-testid="msg-bubbles">
          {page.hasNextPage ? (
            <Button
              size="sm"
              data-testid="msg-show-earlier"
              busy={page.isFetchingNextPage}
              busyLabel={t("messaging.common.loading")}
              onClick={() => {
                skipScroll.current = true;
                void page.fetchNextPage();
              }}
            >
              {t("messaging.thread.showEarlier")}
            </Button>
          ) : null}
          {page.isFetchNextPageError ? (
            <p className="msg-hint" role="alert" data-testid="msg-earlier-failed">
              {t("messaging.thread.messagesError")}
            </p>
          ) : null}
          {page.isPending && messages.length === 0 ? (
            <TableSkeleton rows={4} cols={1} label={t("messaging.common.loading")} />
          ) : null}
          {ordered.map((message, index) => {
            const prev = index > 0 ? ordered[index - 1] : undefined;
            // The day in the clock the bubble prints its time in, never the
            // UTC date the wire carries (D-600).
            const day = timestampDayIso(message.created_at) ?? message.created_at;
            const newDay =
              prev === undefined || (timestampDayIso(prev.created_at) ?? prev.created_at) !== day;
            return (
              <Bubble
                key={message.id}
                message={message}
                provider={headRow.provider}
                locale={locale}
                daySeparator={newDay ? formatBusinessDate(day, locale) : undefined}
                canWork={canWork}
                canManageHeld={canWork}
                onRetry={() => {
                  retry.mutate({ id: message.id, conversationId });
                }}
                retryBusy={retry.isPending}
              />
            );
          })}
          {retry.isError ? (
            <MutationFailureNote
              data-testid="msg-mutation-failed"
              text={t("messaging.thread.retryFailed")}
              error={retry.error}
            />
          ) : null}
        </div>
      )}

      {/* The offer agent on the newest customer message (D-515 ruling 4):
       * its run, the draft chip into the journal, and Draft offer. */}
      <OfferAgentStrip head={headRow} messages={ordered} />

      {!canWork ? null : headRow.provider === "email" ? (
        // D-327 ruling 9: inbound only. One sentence stands where the
        // composer would, and no Send, Attach or template picker renders.
        <p className="msg-hint" data-testid="msg-email-inbound-only">
          {t("messaging.email.inboundOnly")}
        </p>
      ) : (
        <Composer
          conversationId={conversationId}
          head={headRow}
          provider={headRow.provider}
          identityLanguage={headRow.identity.language}
        />
      )}
    </div>
  );
}

function Bubble({
  message,
  provider,
  locale,
  daySeparator,
  canWork,
  canManageHeld,
  onRetry,
  retryBusy,
}: {
  message: ThreadMessage;
  provider: Conversation["provider"];
  locale: string;
  daySeparator: string | undefined;
  canWork: boolean;
  canManageHeld: boolean;
  onRetry: () => void;
  retryBusy: boolean;
}) {
  const { t } = useTranslation();
  if (message.content_kind === "email") {
    return (
      <>
        {daySeparator !== undefined ? (
          <span className="msg-datesep num">{daySeparator}</span>
        ) : null}
        <EmailBubble message={message} locale={locale} canManageHeld={canManageHeld} />
      </>
    );
  }
  const outbound = message.direction === "outbound";
  const failed = message.status === "failed_retryable" || message.status === "failed_permanent";
  // Telegram is sent-terminal (§3.3.6): delivered/read are WA facts and are
  // never rendered for a tg row, whatever a payload claims.
  const shownStatus =
    provider === "telegram" && (message.status === "delivered" || message.status === "read")
      ? "sent"
      : message.status;
  const classes = ["msg-bubble"];
  if (outbound) classes.push("msg-bubble-out");
  if (failed) classes.push("msg-bubble-failed");

  return (
    <>
      {daySeparator !== undefined ? <span className="msg-datesep num">{daySeparator}</span> : null}
      <article
        className={classes.join(" ")}
        data-testid="msg-bubble"
        data-status={message.status}
        data-status-detail={message.status_detail ?? undefined}
        data-direction={message.direction}
        data-sender-kind={message.sender_kind}
        data-template={message.template_code ?? undefined}
      >
        {message.content_kind === "template" && message.template_code !== null ? (
          <StatusChip variant="accent" dot={false} data-testid="msg-template-badge">
            {t("messaging.thread.templateBadge", { code: message.template_code })}
          </StatusChip>
        ) : null}
        {message.media.map((item) => {
          if (item.fetch_status === "pending") {
            return (
              <p key={item.id} className="msg-hint">
                {t("messaging.thread.mediaPending")}
              </p>
            );
          }
          if (item.fetch_status === "expired") {
            return (
              <p key={item.id} className="msg-hint">
                {t("messaging.thread.mediaExpired")}
              </p>
            );
          }
          if (item.download_url === null) {
            return (
              <p key={item.id} className="msg-hint">
                {t("messaging.thread.mediaUnavailable")}
              </p>
            );
          }
          // Inline only for a raster type the registry read from the bytes
          // (D-322 ruling 2). Content-Disposition does not govern an <img>,
          // so the `attachment` the registry serves cannot keep an
          // octet-stream item, or a row from before 0076 with no recorded
          // type, out of the page; this check does.
          if (
            (item.kind === "photo" || item.kind === "sticker") &&
            isInlineImageType(item.mime_type)
          ) {
            return (
              <span key={item.id} className="msg-media-block">
                <img
                  className="msg-media-img"
                  src={item.download_url}
                  alt={item.caption ?? t("messaging.thread.mediaOpen")}
                  data-testid="thread-media-thumb"
                />
                {item.caption !== null ? (
                  <span className="msg-media-caption" data-testid="msg-media-caption">
                    {item.caption}
                  </span>
                ) : null}
              </span>
            );
          }
          return (
            <a
              key={item.id}
              href={item.download_url}
              target="_blank"
              rel="noreferrer"
              data-testid="msg-media-link"
            >
              {item.caption ?? t("messaging.thread.mediaOpen")}
            </a>
          );
        })}
        {message.body !== null && message.body.length > 0 ? (
          <p className="msg-bubble-body" data-testid="msg-bubble-body">
            {/* A Telegram template body is stored as the HTML it was sent
                as; the bubble shows what the customer reads (task #278).
                Typed text is stored as typed and shown as typed. */}
            {message.content_kind === "template" && provider === "telegram"
              ? telegramDisplayText(message.body)
              : message.body}
          </p>
        ) : null}
        {/* D-301: the server masks a sent total or tracking link for a reader
            without sales.read_value, or withholds the body whole when it
            cannot re-render it faithfully. The line says why the text is
            not what the customer read; the flag is the server's, never
            inferred from the text. */}
        {/* D-337 ruling 5: a FORWARDED inbound message is not the customer's
            own words (it is often our own quote sent back), so its text and
            files are withheld whole from the same reader. Its sentence says
            that, rather than the template's "prices and links", which would
            describe a message this one is not. The reason is the server's. */}
        {/* D-585: a credential (the quote's accept link) is masked for every
            reader, a holder of sales values included, so its sentence says
            what was hidden and why rather than "your role". A priced
            document sent with the message (the quote PDF) is said
            separately, from the server's `media_withheld`: it is a file, not
            text, and `value_withheld` speaks only of the text, so a
            document-only message reads one sentence about its file. */}
        {message.value_withheld && message.withheld_reason === "forwarded" ? (
          <p className="msg-hint" data-testid="msg-forward-withheld">
            {t("messaging.thread.forwardedWithheld")}
          </p>
        ) : message.value_withheld && message.withheld_reason === "credential" ? (
          <p className="msg-hint" data-testid="msg-credential-withheld">
            {t("messaging.thread.credentialWithheld")}
          </p>
        ) : message.value_withheld ? (
          <p className="msg-hint" data-testid="msg-value-withheld">
            {message.body === null
              ? t("messaging.thread.bodyWithheld")
              : t("messaging.thread.valueWithheld")}
          </p>
        ) : null}
        {message.media_withheld && message.withheld_reason !== "forwarded" ? (
          <p className="msg-hint" data-testid="msg-attachment-withheld">
            {t("messaging.thread.attachmentWithheld")}
          </p>
        ) : null}
        <span
          className="msg-bubble-meta"
          title={message.status_detail ?? undefined}
          data-testid="msg-bubble-meta"
        >
          {outbound ? <span>{t(`messaging.thread.sender.${message.sender_kind}`)}</span> : null}
          <span className="num">{formatTimestamp(message.created_at, locale)}</span>
          {outbound ? (
            <span data-testid="msg-status-tick" data-status={shownStatus}>
              {t(`messaging.thread.msgStatus.${shownStatus}`)}
            </span>
          ) : null}
          {canWork && message.status === "failed_retryable" ? (
            <Button
              size="sm"
              data-testid="msg-retry"
              busy={retryBusy}
              busyLabel={t("messaging.common.loading")}
              onClick={onRetry}
            >
              {t("messaging.thread.retry")}
            </Button>
          ) : null}
        </span>
        {failed && message.status_detail !== null && provider !== "email" ? (
          <p className="msg-bubble-detail" data-testid="msg-bubble-detail">
            {refusedSendText(t, message.status_detail, provider) ?? message.status_detail}
          </p>
        ) : null}
      </article>
    </>
  );
}

// ---------------------------------------------------------------------------
// composer (§4.1) — free text / attach / template; WA window swap
// ---------------------------------------------------------------------------

function Composer({
  conversationId,
  head,
  provider,
  identityLanguage,
}: {
  conversationId: string;
  head: Conversation;
  provider: ChatProvider;
  identityLanguage: string;
}) {
  const { t } = useTranslation();
  const send = useSendReply();
  const upload = useUploadAttachment();
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<
    readonly { s3Key: string; filename: string; byteSize: number }[]
  >([]);
  const [attachNote, setAttachNote] = useState<"file" | "total" | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // §9.3: the window can close between paint and send — the 409's typed code
  // swaps the composer exactly as a closed-at-paint window does.
  const [windowClosedLate, setWindowClosedLate] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const waClosed = (head.provider === "whatsapp" && !head.wa_window.open) || windowClosedLate;

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file === undefined) return;
    // The Attach button is disabled at the file limit, so "count" is not
    // reachable from the glass; it is refused here all the same.
    const refusal = attachRefusal(
      attachments.map((a) => a.byteSize),
      file.size,
    );
    if (refusal === "file" || refusal === "total") {
      setAttachNote(refusal);
      return;
    }
    if (refusal === "count") return;
    setAttachNote(null);
    upload.mutate(file, {
      onSuccess: (uploaded) => {
        setAttachments((prior) => [...prior, uploaded]);
      },
    });
  };

  const captionLength = captionOverflow(text, attachments.length);
  const captionTooLong = captionLength !== null;
  const atFileLimit = attachments.length >= MEDIA_MAX_FILES;
  const canSend =
    !send.isPending &&
    (text.trim().length > 0 || attachments.length > 0) &&
    !upload.isPending &&
    !captionTooLong;

  const sendFreeForm = () => {
    const body = text.trim();
    send.mutate(
      {
        conversationId,
        body: {
          ...(body.length > 0 ? { body } : {}),
          ...(attachments.length > 0 ? { media_s3_keys: attachments.map((a) => a.s3Key) } : {}),
          // The contract defaults this server-side; the generated client
          // marks defaulted fields required, so the free-form arm states it.
          template_vars: {},
        },
      },
      {
        onSuccess: () => {
          setText("");
          setAttachments([]);
        },
        onError: (error) => {
          if (error instanceof RequestError && error.code === "WA_WINDOW_CLOSED") {
            setWindowClosedLate(true);
            setPickerOpen(true);
          }
        },
      },
    );
  };

  return (
    <div className="msg-composer" data-testid="msg-composer">
      {waClosed ? (
        <div className="msg-toolbar">
          <StatusChip variant="warn" data-testid="msg-session-closed">
            {t("messaging.composer.sessionClosed")}
          </StatusChip>
          <Button
            size="sm"
            variant="primary"
            data-testid="msg-template-open"
            onClick={() => {
              setPickerOpen(true);
            }}
          >
            {t("messaging.composer.templateOpen")}
          </Button>
        </div>
      ) : (
        <>
          {attachments.length > 0 ? (
            <div className="msg-toolbar">
              {attachments.map((a) => (
                <span key={a.s3Key} className="msg-attach-chip" data-testid="msg-attach-chip">
                  {a.filename}
                  <Button
                    size="sm"
                    aria-label={t("messaging.composer.removeAttachment")}
                    onClick={() => {
                      setAttachments((prior) => prior.filter((x) => x.s3Key !== a.s3Key));
                      setAttachNote(null);
                    }}
                  >
                    ✕
                  </Button>
                </span>
              ))}
            </div>
          ) : null}
          <div className="msg-composer-row">
            <textarea
              value={text}
              aria-label={t("messaging.composer.label")}
              placeholder={t("messaging.composer.placeholder")}
              data-testid="msg-composer-input"
              onChange={(e) => {
                setText(e.target.value);
              }}
            />
            <input
              ref={fileRef}
              type="file"
              accept={ATTACH_ACCEPT}
              hidden
              data-testid="msg-attach-input"
              onChange={onFile}
            />
            <Button
              size="sm"
              data-testid="msg-attach"
              disabled={atFileLimit}
              busy={upload.isPending}
              busyLabel={t("messaging.common.loading")}
              onClick={() => {
                fileRef.current?.click();
              }}
            >
              {t("messaging.composer.attach")}
            </Button>
            <Button
              size="sm"
              data-testid="msg-template-open"
              onClick={() => {
                setPickerOpen(true);
              }}
            >
              {t("messaging.composer.templateOpen")}
            </Button>
            <Button
              variant="primary"
              data-testid="msg-send"
              disabled={!canSend}
              busy={send.isPending}
              busyLabel={t("messaging.composer.sending")}
              onClick={sendFreeForm}
            >
              {t("messaging.composer.send")}
            </Button>
          </div>
        </>
      )}

      {attachNote === "file" ? (
        <p className="msg-error-note" role="alert" data-testid="msg-attach-too-large">
          {t("messaging.composer.attachTooLarge")}
        </p>
      ) : null}
      {attachNote === "total" ? (
        <p className="msg-error-note" role="alert" data-testid="msg-attach-total-too-large">
          {t("messaging.composer.attachTotalTooLarge")}
        </p>
      ) : null}
      {atFileLimit && !waClosed ? (
        <p className="msg-hint" data-testid="msg-attach-limit">
          {t("messaging.composer.attachLimit")}
        </p>
      ) : null}
      {captionTooLong && !waClosed ? (
        <p className="msg-error-note" role="alert" data-testid="msg-caption-too-long">
          {t("messaging.composer.captionTooLong", { length: String(captionLength) })}
        </p>
      ) : null}
      {upload.isError ? (
        <MutationFailureNote
          data-testid="msg-mutation-failed"
          text={t("messaging.composer.attachFailed")}
          error={upload.error}
        />
      ) : null}
      {send.isError ? (
        <MutationFailureNote
          data-testid="msg-mutation-failed"
          text={t("messaging.composer.sendFailed")}
          error={send.error}
        />
      ) : null}

      {pickerOpen ? (
        <TemplatePicker
          conversationId={conversationId}
          provider={provider}
          identityLanguage={identityLanguage}
          onClose={() => {
            setPickerOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

/** A variant is SENDABLE when Telegram (`not_required`) or Meta-`approved`
 * (§3.3.2); the picker offers only templates with a sendable variant for the
 * thread's provider, and previews in the identity's language via the same
 * locale chain the server renders with. */
function sendableVariantBody(
  template: MessageTemplate,
  provider: ChatProvider,
  identityLanguage: string,
): string | undefined {
  const sendable = template.variants.filter(
    (v) =>
      v.provider === provider && (v.meta_status === "not_required" || v.meta_status === "approved"),
  );
  if (sendable.length === 0) return undefined;
  // The identity's own language, then the source locale, then the rest of the
  // registry in order (D-430). A tag such as zh-TW reads as Traditional.
  const own = normalizeLocale(identityLanguage) ?? SOURCE_LOCALE;
  const chain = [own, SOURCE_LOCALE, ...SUPPORTED_LOCALES];
  for (const locale of chain) {
    const hit = sendable.find((v) => v.locale === locale);
    if (hit !== undefined) return hit.body;
  }
  return sendable[0]?.body;
}

function TemplatePicker({
  conversationId,
  provider,
  identityLanguage,
  onClose,
}: {
  conversationId: string;
  provider: ChatProvider;
  identityLanguage: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const send = useSendReply();
  // Every ACTIVE template, walked page by page (task #278): the picker
  // filters client-side, so it must hold the whole active set, and a
  // deactivated one is never offered.
  const catalog = useMessagingTemplates({ is_active: "true" }, true);
  const [filter, setFilter] = useState("");
  const [pickedCode, setPickedCode] = useState<string | undefined>(undefined);
  const [vars, setVars] = useState<Record<string, string>>({});

  const options = useMemo(() => {
    const rows = (catalog.data?.items ?? []).filter(
      (tpl) => tpl.is_active && sendableVariantBody(tpl, provider, identityLanguage) !== undefined,
    );
    const term = filter.trim().toLowerCase();
    if (term.length === 0) return rows;
    return rows.filter(
      (tpl) => tpl.code.toLowerCase().includes(term) || tpl.name.toLowerCase().includes(term),
    );
  }, [catalog.data, provider, identityLanguage, filter]);

  const picked = options.find((tpl) => tpl.code === pickedCode);
  const previewBody =
    picked !== undefined ? sendableVariantBody(picked, provider, identityLanguage) : undefined;
  const requiredMissing =
    picked?.variables.some((v) => v.required && (vars[v.key] ?? "").trim().length === 0) ?? false;

  return (
    <Modal
      label={t("messaging.composer.templateTitle")}
      heading={t("messaging.composer.templateTitle")}
      closeLabel={t("messaging.common.close")}
      className="msg-modal"
      testId="msg-template-modal"
      onClose={onClose}
    >
      <div className="msg-composer">
        <Field label={t("messaging.composer.templateSearch")} wide>
          <input
            type="search"
            value={filter}
            data-testid="msg-template-search"
            onChange={(e) => {
              setFilter(e.target.value);
            }}
          />
        </Field>

        {catalog.isPending ? (
          <TableSkeleton rows={3} cols={1} label={t("messaging.common.loading")} />
        ) : catalog.isError ? (
          <ErrorState
            title={t("messaging.templates.error")}
            retryLabel={t("messaging.common.retry")}
            requestId={requestIdOf(catalog.error)}
            requestIdLabel={t("messaging.common.requestId")}
            onRetry={() => {
              void catalog.refetch();
            }}
          />
        ) : options.length === 0 ? (
          <p className="msg-hint" data-testid="msg-template-empty">
            {t("messaging.composer.templateEmpty")}
          </p>
        ) : (
          options.map((tpl) => (
            <button
              key={tpl.id}
              type="button"
              className="msg-template-option"
              aria-pressed={tpl.code === pickedCode}
              data-testid="msg-template-option"
              data-code={tpl.code}
              onClick={() => {
                setPickedCode(tpl.code);
                setVars({});
              }}
            >
              <span className="msg-thread-name">{tpl.name}</span>
              <code>{tpl.code}</code>
            </button>
          ))
        )}

        {picked !== undefined && previewBody !== undefined ? (
          <>
            {picked.variables.length > 0 ? (
              <div className="msg-var-row">
                {picked.variables.map((variable) => (
                  <Field
                    key={variable.key}
                    label={
                      variable.required
                        ? `${variable.key} · ${t("messaging.composer.templateVarRequired")}`
                        : variable.key
                    }
                  >
                    <input
                      value={vars[variable.key] ?? ""}
                      placeholder={variable.sample}
                      data-testid={`msg-template-var-${variable.key}`}
                      onChange={(e) => {
                        setVars((prior) => ({ ...prior, [variable.key]: e.target.value }));
                      }}
                    />
                  </Field>
                ))}
              </div>
            ) : null}
            <p className="msg-preview" data-testid="msg-template-preview">
              <TemplatePreview
                body={previewBody}
                provider={provider}
                variables={picked.variables}
                values={vars}
              />
            </p>
            <div className="msg-toolbar">
              <Button
                variant="primary"
                data-testid="msg-template-send"
                disabled={requiredMissing || send.isPending}
                busy={send.isPending}
                busyLabel={t("messaging.composer.sending")}
                onClick={() => {
                  const filled: Record<string, string> = {};
                  for (const variable of picked.variables) {
                    const value = (vars[variable.key] ?? "").trim();
                    if (value.length > 0) filled[variable.key] = value;
                  }
                  send.mutate(
                    {
                      conversationId,
                      body: { template_code: picked.code, template_vars: filled },
                    },
                    { onSuccess: onClose },
                  );
                }}
              >
                {t("messaging.composer.templateSend")}
              </Button>
            </div>
          </>
        ) : null}

        {send.isError ? (
          <MutationFailureNote
            data-testid="msg-mutation-failed"
            text={t("messaging.composer.sendFailed")}
            error={send.error}
          />
        ) : null}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// right pane — context (§4.1) + claim (§4.2)
// ---------------------------------------------------------------------------

function ContextPanel({
  head,
  canWork,
  isAdmin,
  onCloseSheet,
}: {
  head: Conversation;
  canWork: boolean;
  isAdmin: boolean;
  onCloseSheet: () => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const patchAssign = usePatchConversation();
  const patchSnooze = usePatchConversation();
  const patchStatus = usePatchConversation();
  const members = useMemberIndex(isAdmin && canWork);
  const unclaimed = head.kind === "customer" && head.customer_id === null;

  const resetAll = () => {
    patchAssign.reset();
    patchSnooze.reset();
    patchStatus.reset();
  };

  return (
    <aside
      className="msg-context"
      aria-label={t("messaging.context.aria")}
      data-testid="msg-context"
    >
      <div className="msg-toolbar msg-sheet-toolbar">
        <Button size="sm" data-testid="msg-context-close" onClick={onCloseSheet}>
          {t("messaging.common.close")}
        </Button>
      </div>

      <section className="ds-card">
        <h3>{t("messaging.context.customerTitle")}</h3>
        {head.customer !== null ? (
          <>
            <p className="msg-fact" data-testid="msg-context-customer">
              {head.customer.display_name}
            </p>
            <Button
              size="sm"
              data-testid="msg-open-customer"
              onClick={() => {
                // A navigation CTA is a Button whose onClick navigates (gate
                // §4.3b) — through the router, never a full-page reload.
                void navigate({
                  to: "/sales/customers/$customerId",
                  params: { customerId: head.customer_id ?? "" },
                });
              }}
            >
              {t("messaging.context.openCustomer")}
            </Button>
          </>
        ) : unclaimed && canWork ? (
          <ClaimPanel head={head} />
        ) : (
          <p className="msg-hint">{t("messaging.context.noCustomer")}</p>
        )}
        <CreateQuoteLink customerId={head.customer_id} />
        <CreateDealLink customerId={head.customer_id} conversationId={head.id} />
      </section>

      {/* §4.1's linked-orders block. A conversation with no customer has no
       * order list to show — not an empty one, which would read as "this
       * customer has no orders" about a customer nobody has named yet. */}
      {head.customer_id !== null ? (
        <section className="ds-card" data-testid="msg-linked-orders">
          <h3>{t("messaging.context.ordersTitle")}</h3>
          <LinkedOrders head={head} customerId={head.customer_id} canWork={canWork} />
        </section>
      ) : null}

      <section className="ds-card">
        <h3>{t("messaging.context.identityTitle")}</h3>
        <p className="msg-fact">
          {t(`messaging.provider.${head.provider}`)}{" "}
          {/* §4.1: the identity block renders the MASKED form for every role
           * (displayAddress mirrors the server's one mask) — the full value
           * stays on the wire for `messaging.address.read` holders, never
           * painted onto the shared screen. */}
          <code data-testid="msg-identity-address">
            {displayAddress(head.provider, head.identity.address)}
          </code>
        </p>
        <p className="msg-fact">
          {t("messaging.context.language")}: {head.identity.language}
        </p>
        {head.identity.verified_at !== null ? (
          <p className="msg-fact num">
            {t("messaging.context.verifiedAt", {
              date: formatTimestamp(head.identity.verified_at, i18n.language),
            })}
          </p>
        ) : (
          <p className="msg-hint">{t("messaging.context.pendingIdentity")}</p>
        )}
        <div className="msg-thread-chips">
          {/* ONE element carries both §2.2 flags as attributes — the
           * walkthrough contract reads data-marketing/data-hard off it
           * (M17-S13); the label states the stronger fact. */}
          {head.identity.opted_out || head.identity.opted_out_hard ? (
            <StatusChip
              variant={head.identity.opted_out_hard ? "bad" : "warn"}
              data-testid="msg-identity-optout"
              data-marketing={String(head.identity.opted_out)}
              data-hard={String(head.identity.opted_out_hard)}
            >
              {head.identity.opted_out_hard
                ? t("messaging.context.optedOutHard")
                : t("messaging.context.optedOut")}
            </StatusChip>
          ) : null}
        </div>
      </section>

      {canWork ? (
        <section className="ds-card" data-testid="msg-actions">
          <h3>{t("messaging.context.assignLabel")}</h3>
          {isAdmin ? (
            <Field label={t("messaging.context.assignLabel")} data-testid="msg-assign-field">
              <Select
                value={head.assigned_to ?? ""}
                data-testid="msg-assign"
                onChange={(e) => {
                  resetAll();
                  patchAssign.mutate({
                    id: head.id,
                    body: { assigned_to: e.target.value === "" ? null : e.target.value },
                  });
                }}
              >
                <option value="">{t("messaging.context.assignNone")}</option>
                {members.members.map((member) => (
                  <option key={member.user_id} value={member.user_id}>
                    {member.display_name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : (
            <>
              {head.assigned_to !== null ? (
                <p className="msg-fact" data-testid="msg-assigned-to">
                  {/* The member directory is an admin read; for everyone else
                      the names read (`GET /v1/refs`) names the assignee, and
                      a person it may not name stays the short-id chip. */}
                  <RefLabel
                    value={members.byId.get(head.assigned_to)}
                    id={head.assigned_to}
                    pending={members.isPending}
                    kind="user"
                  />
                </p>
              ) : (
                <p className="msg-hint">{t("messaging.context.assignNone")}</p>
              )}
              <Button
                size="sm"
                data-testid="msg-assign-me"
                onClick={() => {
                  resetAll();
                  // The P3-0-adjudicated `"me"` literal arm: resolved
                  // server-side, so a sales seat needs no directory read and
                  // no self-id feed.
                  patchAssign.mutate({ id: head.id, body: { assigned_to: "me" } });
                }}
              >
                {t("messaging.context.assignMe")}
              </Button>
            </>
          )}
          {patchAssign.isError ? (
            <MutationFailureNote
              data-testid="msg-mutation-failed"
              text={t("messaging.context.assignFailed")}
              error={patchAssign.error}
            />
          ) : null}

          <h3>{t("messaging.context.snoozeLabel")}</h3>
          <div className="msg-toolbar">
            {(
              [
                ["2h", "msg-snooze-2h", "messaging.context.snooze2h"],
                ["tomorrow", "msg-snooze-tomorrow", "messaging.context.snoozeTomorrow"],
                ["next_week", "msg-snooze-next-week", "messaging.context.snoozeNextWeek"],
              ] as const
            ).map(([option, testid, key]) => (
              <Button
                key={option}
                size="sm"
                data-testid={testid}
                onClick={() => {
                  resetAll();
                  patchSnooze.mutate({
                    id: head.id,
                    body: { status: "snoozed", snoozed_until: snoozeUntil(option) },
                  });
                }}
              >
                {t(key)}
              </Button>
            ))}
          </div>
          {head.status === "snoozed" && head.snoozed_until !== null ? (
            <p className="msg-fact num">
              {t("messaging.context.snoozedUntil", {
                date: formatTimestamp(head.snoozed_until, i18n.language),
              })}
            </p>
          ) : null}
          {patchSnooze.isError ? (
            <MutationFailureNote
              data-testid="msg-mutation-failed"
              text={t("messaging.context.snoozeFailed")}
              error={patchSnooze.error}
            />
          ) : null}

          <div className="msg-toolbar">
            {head.status === "closed" ? (
              <Button
                size="sm"
                data-testid="msg-reopen"
                onClick={() => {
                  resetAll();
                  patchStatus.mutate({ id: head.id, body: { status: "open" } });
                }}
              >
                {t("messaging.context.reopenThread")}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="danger"
                data-testid="msg-close-thread"
                onClick={() => {
                  resetAll();
                  patchStatus.mutate({ id: head.id, body: { status: "closed" } });
                }}
              >
                {t("messaging.context.closeThread")}
              </Button>
            )}
          </div>
          {patchStatus.isError ? (
            <MutationFailureNote
              data-testid="msg-mutation-failed"
              text={t("messaging.context.closeFailed")}
              error={patchStatus.error}
            />
          ) : null}
        </section>
      ) : null}
    </aside>
  );
}

/**
 * §4.1's "Create quote" door: the composer, opened already pointed at this
 * conversation's customer (`/sales/quotes/new?customer_id=…`).
 *
 * TWO conditions, and each failure is ABSENCE or a sentence, never a disabled
 * control (D-088):
 *   - without `sales.quote.write` nothing renders at all. The read-only trio
 *     and the floor roles never see a door they would be refused at, and the
 *     gate is the SALES mirror because that is the code the composer's own
 *     route carries — `canWork` here is `messaging.conversation.manage`,
 *     which says nothing about quoting.
 *   - without a customer the pane says what to do first, because a quote
 *     needs somebody to quote to: the §4.2 claim above is that act.
 */
function CreateQuoteLink({ customerId }: { customerId: string | null }) {
  const { t } = useTranslation();
  const sales = useSalesPermissions();
  if (!sales.has("sales.quote.write")) return null;
  if (customerId === null) {
    return (
      <p className="msg-hint" data-testid="msg-create-quote-needs-customer">
        {t("messaging.context.createQuoteNeedsCustomer")}
      </p>
    );
  }
  return (
    <p className="msg-order-row">
      <Link
        to="/sales/quotes/new"
        search={{ customer_id: customerId }}
        data-testid="msg-create-quote"
      >
        {t("messaging.context.createQuote")}
      </Link>
    </p>
  );
}

/**
 * §4.1's Create deal door (spec 09 §4.14, spec 17 §4.1; P3-E2 PR E2-1): beside
 * Create quote, for a holder of `sales.opportunity.write` on a thread with a
 * customer. It opens the board with New deal open, the customer locked and
 * the conversation recorded (`/sales/pipeline?new_deal=…&from_conversation=…`).
 * It prints no count, because no read serves one. A thread with no customer
 * shows no door: Create quote's link-first sentence already says what to do,
 * and it is rendered above this for any quote writer; a deal writer who is
 * not one (none today, the three roles hold both codes) reads nothing here.
 */
function CreateDealLink({
  customerId,
  conversationId,
}: {
  customerId: string | null;
  conversationId: string;
}) {
  const { t } = useTranslation();
  const sales = useSalesPermissions();
  if (!canWriteOpportunities(sales) || customerId === null) return null;
  return (
    <p className="msg-order-row">
      <Link
        to="/sales/pipeline"
        search={{ new_deal: customerId, from_conversation: conversationId }}
        data-testid="msg-create-deal"
      >
        {t("messaging.context.createDeal")}
      </Link>
    </p>
  );
}

/** How many of the customer's orders the pane lists. The block answers "which
 * order is this message about", which the recent ones do; the order book is
 * one link away for the rest. */
const LINKED_ORDER_LIMIT = 5;

/**
 * §4.1's linked-orders list — the customer's most recent orders, read from
 * module 09's own order book (`GET /v1/orders?customer_id=…`).
 *
 * NO MONEY RENDERS HERE. The route narrows to assigned customers for a sales
 * seat and drops the value fields for a caller without `sales.read_value`, so
 * a total on this pane would be blank for some readers and a leak past the
 * field grant for others — and "which order is this about" needs a number, a
 * state and a date, not a sum.
 *
 * A caller without `sales.read` gets a sentence and NO REQUEST: firing a read
 * the server will refuse is D-088's dead call, and the pane would paint an
 * error card over a fact about the reader's role.
 */
function LinkedOrders({
  head,
  customerId,
  canWork,
}: {
  head: Conversation;
  customerId: string;
  canWork: boolean;
}) {
  const { t, i18n } = useTranslation();
  const sales = useSalesPermissions();
  const maySeeOrders = sales.has("sales.read");
  const page = useOrders(
    { customer_id: customerId, limit: LINKED_ORDER_LIMIT },
    undefined,
    maySeeOrders,
  );
  const patchPrimary = usePatchConversation();
  const rows = page.data?.items ?? [];

  if (isSalesReadRefused(sales, maySeeOrders)) {
    return (
      <p className="msg-hint" data-testid="msg-orders-hidden">
        {t("messaging.context.ordersHidden")}
      </p>
    );
  }
  if (page.isPending) {
    return <TableSkeleton rows={3} cols={1} label={t("messaging.common.loading")} />;
  }
  if (page.error !== null) {
    return (
      <ErrorState
        title={t("messaging.context.ordersError")}
        retryLabel={t("messaging.common.retry")}
        requestId={requestIdOf(page.error)}
        requestIdLabel={t("messaging.common.requestId")}
        onRetry={() => {
          void page.refetch();
        }}
      />
    );
  }

  return (
    <>
      {rows.length === 0 ? (
        <p className="msg-hint" data-testid="msg-orders-empty">
          {t("messaging.context.ordersEmpty")}
        </p>
      ) : (
        rows.map((order) => (
          <p className="msg-order-row" key={order.id} data-testid="msg-linked-order">
            <Link
              to="/sales/orders/$orderId"
              params={{ orderId: order.id }}
              data-testid="msg-linked-order-no"
            >
              {order.order_no}
            </Link>
            <StatusChip variant={orderStatusVariant(order.status)} data-status={order.status}>
              {t(`sales.orders.status.${order.status}`)}
            </StatusChip>
            <span className="msg-fact num">
              {formatTimestamp(order.status_at ?? order.created_at, i18n.language)}
            </span>
            {order.id === head.primary_order_id ? (
              <StatusChip variant="neutral" dot={false} data-testid="msg-primary-order">
                {t("messaging.context.primaryChip")}
              </StatusChip>
            ) : canWork ? (
              <Button
                size="sm"
                data-testid="msg-set-primary-order"
                onClick={() => {
                  patchPrimary.reset();
                  patchPrimary.mutate({ id: head.id, body: { primary_order_id: order.id } });
                }}
              >
                {t("messaging.context.setPrimaryOrder")}
              </Button>
            ) : null}
          </p>
        ))
      )}
      {/* The primary order need not be among the five most recent — an old
       * order can stay the thread's subject for months. Its reference still
       * shows, marked, rather than silently leaving the pane. */}
      {head.primary_order_id !== null && !rows.some((o) => o.id === head.primary_order_id) ? (
        <p className="msg-order-row">
          {/* The order is off the five-row list, so its number is not on
              this page: a link that opens it, never the start of its id. */}
          <Link
            to="/sales/orders/$orderId"
            params={{ orderId: head.primary_order_id }}
            data-testid="msg-primary-order"
          >
            {t("messaging.context.primaryOrderOpen")}
          </Link>
          <StatusChip variant="neutral" dot={false}>
            {t("messaging.context.primaryChip")}
          </StatusChip>
        </p>
      ) : null}
      {patchPrimary.isError ? (
        <MutationFailureNote
          data-testid="msg-mutation-failed"
          text={t("messaging.context.setPrimaryFailed")}
          error={patchPrimary.error}
        />
      ) : null}
    </>
  );
}

/** §4.2's claim: search module-09 customers, pick the CONTACT (§5's claim arm
 * names `customer_contact_id`), attach — or Mark spam (close + revoke, reason
 * `spam`). */
function ClaimPanel({ head }: { head: Conversation }) {
  const { t } = useTranslation();
  const [term, setTerm] = useState("");
  const debounced = useDebouncedValue(term, 300);
  const [customerId, setCustomerId] = useState("");
  const [contactId, setContactId] = useState("");
  const customers = useCustomerSearch(debounced, true);
  const picked = useCustomerContacts(customerId);
  const claim = usePatchConversation();
  const spam = usePatchConversation();
  const [confirmSpam, setConfirmSpam] = useState(false);

  // Only ACTIVE contacts are claimable: archival deletes channels and the
  // seam consumer revokes identities, so attaching to an archived contact
  // would mint a binding the next sweep tears down.
  const contacts = (picked.data?.contacts ?? []).filter((c) => c.status === "active");

  return (
    <div className="msg-link-block" data-testid="msg-claim">
      {head.provider === "email" && displayNameHoldsAddress(head.identity) ? (
        <p className="msg-error-note" data-testid="msg-claim-name-flag">
          {t("messaging.email.nameHoldsAddress", { address: head.identity.address })}
        </p>
      ) : null}
      <Field label={t("messaging.context.attachSearch")} wide>
        <input
          type="search"
          value={term}
          placeholder={t("messaging.context.attachHint")}
          data-testid="msg-claim-search"
          onChange={(e) => {
            setTerm(e.target.value);
            setCustomerId("");
            setContactId("");
          }}
        />
      </Field>
      {debounced.trim().length > 0 ? (
        customers.isPending ? (
          <p className="msg-hint">{t("messaging.common.loading")}</p>
        ) : (customers.data ?? []).length === 0 ? (
          <p className="msg-hint" data-testid="msg-claim-none">
            {t("messaging.context.attachNone")}
          </p>
        ) : (
          <Field label={t("messaging.context.customerTitle")}>
            <Select
              value={customerId}
              data-testid="msg-claim-customer"
              onChange={(e) => {
                setCustomerId(e.target.value);
                setContactId("");
              }}
            >
              <option value="" />
              {(customers.data ?? []).map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.display_name}
                </option>
              ))}
            </Select>
          </Field>
        )
      ) : null}
      {customerId.length > 0 ? (
        <Field label={t("messaging.context.attachTitle")}>
          <Select
            value={contactId}
            data-testid="msg-claim-contact"
            onChange={(e) => {
              setContactId(e.target.value);
            }}
          >
            <option value="" />
            {contacts.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.full_name}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
      <div className="msg-toolbar">
        <Button
          size="sm"
          variant="primary"
          data-testid="msg-claim-attach"
          disabled={contactId.length === 0 || claim.isPending}
          busy={claim.isPending}
          busyLabel={t("messaging.common.saving")}
          onClick={() => {
            spam.reset();
            claim.mutate({ id: head.id, body: { customer_contact_id: contactId } });
          }}
        >
          {t("messaging.context.attachConfirm")}
        </Button>
        <Button
          size="sm"
          variant="danger"
          data-testid="msg-spam"
          onClick={() => {
            setConfirmSpam(true);
          }}
        >
          {t("messaging.context.markSpam")}
        </Button>
      </div>
      {claim.isError ? (
        <MutationFailureNote
          data-testid="msg-mutation-failed"
          text={t("messaging.context.attachFailed")}
          error={claim.error}
        />
      ) : null}
      {spam.isError ? (
        <MutationFailureNote
          data-testid="msg-mutation-failed"
          text={t("messaging.context.spamFailed")}
          error={spam.error}
        />
      ) : null}

      {confirmSpam ? (
        <SpamConfirm
          head={head}
          spam={spam}
          onDone={() => {
            setConfirmSpam(false);
          }}
        />
      ) : null}
    </div>
  );
}

function SpamConfirm({
  head,
  spam,
  onDone,
}: {
  head: Conversation;
  spam: ReturnType<typeof usePatchConversation>;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  // §4.2: Mark spam = close the thread + revoke the sender identity (reason
  // `spam`). Revoke first — the identity is the thing spam is ABOUT — then
  // close; each failure renders its own note.
  const revoke = useRevokeIdentity();
  return (
    <Modal
      label={t("messaging.context.spamTitle")}
      heading={t("messaging.context.spamTitle")}
      closeLabel={t("messaging.common.close")}
      className="msg-modal"
      testId="msg-spam-confirm"
      onClose={onDone}
    >
      <div className="msg-link-block">
        <p className="msg-fact">{t("messaging.context.spamBody")}</p>
        <div className="msg-toolbar">
          <Button size="sm" onClick={onDone}>
            {t("messaging.common.cancel")}
          </Button>
          <Button
            size="sm"
            variant="danger"
            data-testid="msg-spam-confirm-btn"
            busy={revoke.isPending || spam.isPending}
            busyLabel={t("messaging.common.saving")}
            onClick={() => {
              revoke.mutate(
                { id: head.identity.id, reason: "spam" },
                {
                  onSuccess: () => {
                    spam.mutate({ id: head.id, body: { status: "closed" } }, { onSuccess: onDone });
                  },
                },
              );
            }}
          >
            {t("messaging.context.markSpam")}
          </Button>
        </div>
        {revoke.isError ? (
          <MutationFailureNote
            data-testid="msg-mutation-failed"
            text={t("messaging.context.spamFailed")}
            error={revoke.error}
          />
        ) : null}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// email (P3-E2 PR E2-2e, D-327): the message, its files, the triage card and
// the Unverified mail list
// ---------------------------------------------------------------------------

/** Why a part was neither stored nor held, as a sentence (§2.14). A typed
 * record, so a reason the contract adds fails the build here rather than
 * reaching a manager as a raw token. */
const OMITTED_KEY: Readonly<Record<EmailOmittedPart["reason"], string>> = {
  virus_not_clean: "messaging.email.omitted.virus_not_clean",
  spam: "messaging.email.omitted.spam",
  too_large: "messaging.email.omitted.too_large",
  type_not_allowed: "messaging.email.omitted.type_not_allowed",
  bytes_mismatch: "messaging.email.omitted.bytes_mismatch",
  expired_before_linking: "messaging.email.omitted.expired_before_linking",
};

/** The mark every email message carries (D-327 ruling 6). DMARC proves the
 * domain and never the mailbox, so the verified mark names the domain. */
function EmailMark({ verified, testId }: { verified: boolean; testId: string }) {
  const { t } = useTranslation();
  return (
    <StatusChip
      variant={verified ? "ok" : "warn"}
      data-testid={testId}
      data-verified={String(verified)}
    >
      {verified ? t("messaging.email.verified") : t("messaging.email.notVerified")}
    </StatusChip>
  );
}

/**
 * One email message (§4.1's email paragraph). The subject stands above the
 * body, the mark and the releaser beside it, and the body is plain text: the
 * server converts an HTML-only mail to text and the office never renders
 * HTML (D-327 ruling 16). The quoted or forwarded part is folded under
 * "Earlier message" when the server served it to this reader, and a reader
 * it withheld it from reads "Earlier message hidden" from `has_quoted_text`,
 * the server's flag, never a guess from the text (D-327 ruling 12).
 */
function EmailBubble({
  message,
  locale,
  canManageHeld,
}: {
  message: ThreadMessage;
  locale: string;
  canManageHeld: boolean;
}) {
  const { t } = useTranslation();
  const quoted = message.quoted_body;
  return (
    <article
      className="msg-bubble"
      data-testid="msg-bubble"
      data-kind="email"
      data-status={message.status}
      data-direction={message.direction}
      data-sender-kind={message.sender_kind}
      data-receipt-id={message.email_receipt_id ?? undefined}
    >
      <p className="msg-bubble-body" data-testid="msg-email-subject">
        <strong>{message.subject ?? t("messaging.email.noSubject")}</strong>
      </p>
      <span className="msg-thread-chips">
        <EmailMark verified={message.sender_verified === true} testId="msg-email-mark" />
        {message.released_by_name !== null ? (
          <StatusChip variant="neutral" dot={false} data-testid="msg-email-released">
            {t("messaging.email.releasedBy", { name: message.released_by_name })}
          </StatusChip>
        ) : null}
      </span>
      {message.body !== null && message.body.length > 0 ? (
        <p className="msg-bubble-body" data-testid="msg-bubble-body">
          {message.body}
        </p>
      ) : null}
      {message.body_truncated ? (
        <p className="msg-hint" data-testid="msg-email-truncated">
          {t("messaging.email.truncated")}
        </p>
      ) : null}
      {typeof quoted === "string" ? (
        <details data-testid="msg-email-quoted">
          <summary className="msg-hint">{t("messaging.email.earlier")}</summary>
          <p className="msg-bubble-body" data-testid="msg-email-quoted-body">
            {quoted}
          </p>
        </details>
      ) : message.has_quoted_text ? (
        <p className="msg-hint" data-testid="msg-email-quoted-hidden">
          {t("messaging.email.earlierHidden")}
        </p>
      ) : null}
      <EmailFiles message={message} locale={locale} canManageHeld={canManageHeld} />
      {/* D-337 ruling 5 on email: the server withheld a forward's subject
          and files from this reader and says so; the flag is the server's. */}
      {message.value_withheld && message.withheld_reason === "forwarded" ? (
        <p className="msg-hint" data-testid="msg-email-forward-withheld">
          {t("messaging.email.forwardWithheld")}
        </p>
      ) : null}
      <span className="msg-bubble-meta" data-testid="msg-bubble-meta">
        <span className="num">{formatTimestamp(message.created_at, locale)}</span>
      </span>
    </article>
  );
}

/** An email's files, stacked under its body (§4.1): stored files with their
 * name, type and size and a Download that reads the thread's media link,
 * held files waiting until the sender is linked, and omitted files with
 * their reason. Renders nothing for a mail with no part. */
function EmailFiles({
  message,
  locale,
  canManageHeld,
}: {
  message: ThreadMessage;
  locale: string;
  canManageHeld: boolean;
}) {
  const { t } = useTranslation();
  const count = message.media.length + message.held_parts.length + message.omitted_parts.length;
  if (count === 0) return null;
  return (
    <div
      className="msg-link-block"
      role="group"
      aria-label={t("messaging.email.filesAria")}
      data-testid="msg-email-files"
    >
      {message.media.map((item) => (
        <p className="msg-order-row" key={item.id} data-testid="msg-email-file">
          <span className="msg-fact" data-testid="msg-email-file-name">
            {item.file_name ?? t("messaging.email.fileUnnamed")}
          </span>
          {item.mime_type !== null ? (
            <span className="msg-fact">{typeLabel(item.mime_type)}</span>
          ) : null}
          {item.byte_size !== null ? (
            <span className="msg-fact num">{formatSize(item.byte_size, locale)}</span>
          ) : null}
          {item.download_url !== null ? (
            <a
              href={item.download_url}
              target="_blank"
              rel="noreferrer"
              data-testid="msg-email-file-download"
            >
              {t("messaging.email.download")}
            </a>
          ) : (
            <span className="msg-hint">{t("messaging.thread.mediaUnavailable")}</span>
          )}
        </p>
      ))}
      {message.held_parts.map((part) => (
        <HeldPartRow
          key={`${part.receipt_id}:${String(part.index)}`}
          part={part}
          locale={locale}
          waiting
          canDownload={canManageHeld}
        />
      ))}
      {message.omitted_parts.map((part) => (
        <p
          className="msg-order-row"
          key={`omitted:${String(part.index)}`}
          data-testid="msg-email-omitted"
          data-reason={part.reason}
        >
          <span className="msg-fact">{part.name ?? t("messaging.email.fileUnnamed")}</span>
          <span className="msg-hint">{t(OMITTED_KEY[part.reason])}</span>
        </p>
      ))}
    </div>
  );
}

/** One held part, on a thread message or an Unverified mail row. Download
 * mints the part's 5-minute link through the held-part route, addressed by
 * the receipt that holds it and the part's index, and starts the browser's
 * download; it is offered only to holders of `messaging.conversation.manage`,
 * the route's own audience. */
function HeldPartRow({
  part,
  locale,
  waiting,
  canDownload,
}: {
  part: EmailHeldPart;
  locale: string;
  waiting: boolean;
  canDownload: boolean;
}) {
  const { t } = useTranslation();
  const link = useHeldPartLink();
  return (
    <>
      <p
        className="msg-order-row"
        data-testid="msg-email-held-part"
        data-receipt-id={part.receipt_id}
        data-index={part.index}
      >
        <span className="msg-fact" data-testid="msg-email-held-name">
          {part.name ?? t("messaging.email.fileUnnamed")}
        </span>
        <span className="msg-fact">{typeLabel(part.content_type)}</span>
        <span className="msg-fact num">{formatSize(part.byte_size, locale)}</span>
        {waiting ? <span className="msg-hint">{t("messaging.email.heldWaiting")}</span> : null}
        {canDownload ? (
          <Button
            size="sm"
            data-testid="msg-email-held-download"
            busy={link.isPending}
            busyLabel={t("messaging.email.downloading")}
            onClick={() => {
              link.mutate(
                { receiptId: part.receipt_id, index: part.index },
                {
                  onSuccess: (minted) => {
                    startDownload(minted.url);
                  },
                },
              );
            }}
          >
            {t("messaging.email.download")}
          </Button>
        ) : null}
      </p>
      {link.isError ? (
        <MutationFailureNote
          data-testid="msg-mutation-failed"
          text={t("messaging.email.downloadFailed")}
          error={link.error}
        />
      ) : null}
    </>
  );
}

/**
 * The email triage card (§4.2, spec 09 §4.11): it heads an unclaimed email
 * thread with the channel, the first message's subject, the sender and the
 * mark. The sender is painted as served, so a reader without the address
 * grant reads the masked address and no name, and the flag for a display
 * name that holds `@` reaches only a reader served the name (D-327 ruling
 * 20). The conversation list serves no subject, so the card reads it from
 * the oldest message the pane has loaded, which on a triage thread is the
 * first.
 */
function EmailTriageCard({
  head,
  ordered,
}: {
  head: Conversation;
  ordered: readonly ThreadMessage[];
}) {
  const { t } = useTranslation();
  const first = ordered.find((m) => m.content_kind === "email");
  return (
    <section
      className="msg-preview msg-link-block"
      aria-label={t("messaging.email.triageTitle")}
      data-testid="msg-email-triage"
    >
      <p className="msg-fact">
        <strong>{t("messaging.email.triageTitle")}</strong>
      </p>
      <p className="msg-fact" data-testid="msg-email-triage-channel">
        {t("messaging.email.channel")}: {t("messaging.provider.email")}
      </p>
      {first !== undefined ? (
        <p className="msg-fact" data-testid="msg-email-triage-subject">
          {t("messaging.email.subject")}: {first.subject ?? t("messaging.email.noSubject")}
        </p>
      ) : null}
      <p className="msg-fact" data-testid="msg-email-triage-sender">
        {t("messaging.email.sender")}: {emailSenderLabel(t, head.identity)}
      </p>
      {displayNameHoldsAddress(head.identity) ? (
        <p className="msg-error-note" data-testid="msg-email-triage-name-flag">
          {t("messaging.email.nameHoldsAddress", { address: head.identity.address })}
        </p>
      ) : null}
      {first !== undefined ? (
        <span className="msg-thread-chips">
          <EmailMark verified={first.sender_verified === true} testId="msg-email-triage-mark" />
        </span>
      ) : null}
    </section>
  );
}

/**
 * §4.2's Unverified mail (D-327 rulings 7 and 18): the held mail from known
 * customers whose sender domain did not verify, newest first, each a card
 * with its two actions under it at every width. Rendered only for holders of
 * `messaging.conversation.manage` (the screen gives no tab to anyone else).
 * Offline disables Release and Discard and says so; a 409
 * EMAIL_RECEIPT_NOT_HELD means another person decided the mail, and the list
 * reads again and says that.
 */
function UnverifiedMailList({
  query,
  onOpenThread,
}: {
  query: ReturnType<typeof useEmailQuarantine>;
  onOpenThread: (conversationId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const online = useMessagingOnline();
  const [decidedElsewhere, setDecidedElsewhere] = useState(false);
  // One page at a time (design-system §3.17 rev-7): the held mail is read 25
  // to a page, newest first, and Newer and Older move between the pages.
  const pages = useLoadedPager<EmailQuarantineItem>(query, "held");
  const rows = pages.view.items;

  if (query.isPending) {
    if (query.fetchStatus === "paused") {
      return (
        <OfflineState title={t("messaging.offline.title")} body={t("messaging.offline.body")} />
      );
    }
    return <TableSkeleton rows={4} cols={1} label={t("messaging.common.loading")} />;
  }
  if (query.isError && rows.length === 0) {
    return (
      <ErrorState
        title={t("messaging.email.held.error")}
        retryLabel={t("messaging.common.retry")}
        requestId={requestIdOf(query.error)}
        requestIdLabel={t("messaging.common.requestId")}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <div
      className="msg-link-block"
      role="list"
      aria-label={t("messaging.email.held.listAria")}
      data-testid="msg-held-list"
    >
      <p className="msg-hint">{t("messaging.email.held.intro")}</p>
      {!online ? (
        <p className="msg-hint" role="status" data-testid="msg-held-offline">
          {t("messaging.email.held.offline")}
        </p>
      ) : null}
      {decidedElsewhere ? (
        <p className="msg-hint" role="status" data-testid="msg-held-decided-elsewhere">
          {t("messaging.email.held.decidedElsewhere")}
        </p>
      ) : null}
      {rows.length === 0 ? (
        <p className="msg-hint" data-testid="msg-held-empty">
          {t("messaging.email.held.empty")}
        </p>
      ) : (
        rows.map((item) => (
          <HeldMailCard
            key={item.id}
            item={item}
            locale={i18n.language}
            online={online}
            onOpenThread={onOpenThread}
            onDecidedElsewhere={() => {
              setDecidedElsewhere(true);
            }}
          />
        ))
      )}
      <MsgPager
        range={pageRangeLabel(t, i18n.language, pages.view)}
        hasPrev={pages.view.hasPrev}
        hasNext={pages.view.hasNext}
        busy={pages.turning}
        busyLabel={t("messaging.common.loading")}
        prevLabel={t("messaging.common.pageNewer")}
        nextLabel={t("messaging.common.pageOlder")}
        onPrev={pages.prev}
        onNext={pages.next}
        testId="msg-held-pager"
      />
      {query.isFetchNextPageError ? (
        <p className="msg-hint" role="alert" data-testid="msg-held-more-failed">
          {t("messaging.email.held.error")}
        </p>
      ) : null}
    </div>
  );
}

function HeldMailCard({
  item,
  locale,
  online,
  onOpenThread,
  onDecidedElsewhere,
}: {
  item: EmailQuarantineItem;
  locale: string;
  online: boolean;
  onOpenThread: (conversationId: string) => void;
  onDecidedElsewhere: () => void;
}) {
  const { t } = useTranslation();
  const [confirm, setConfirm] = useState<"release" | "discard" | null>(null);
  const conversationId = item.conversation_id;
  return (
    <article
      className="msg-thread"
      role="listitem"
      data-testid="msg-held-row"
      data-receipt-id={item.id}
      data-mailbox-count={item.mailbox_count}
    >
      <div className="msg-health-row">
        <span className="msg-provider">{t("messaging.provider.email")}</span>
        <span className="msg-fact num" data-testid="msg-held-received">
          {t("messaging.email.held.received", {
            time: formatTimestamp(item.received_at, locale),
          })}
        </span>
      </div>
      <p className="msg-bubble-body" data-testid="msg-held-subject">
        <strong>{item.subject ?? t("messaging.email.noSubject")}</strong>
      </p>
      {/* The From's mailboxes as served: in clear for a holder of the
          address grant, masked for every other reader (§7). */}
      <div className="msg-link-block" data-testid="msg-held-senders">
        {item.senders.map((sender, index) => (
          <code className="msg-fact" key={`${String(index)}:${sender}`}>
            {sender}
          </code>
        ))}
      </div>
      {item.customer_display_name !== null ? (
        <p className="msg-order-row">
          <span className="msg-fact" data-testid="msg-held-customer">
            {item.customer_display_name}
          </span>
          {conversationId !== null ? (
            <Button
              size="sm"
              data-testid="msg-held-open"
              onClick={() => {
                onOpenThread(conversationId);
              }}
            >
              {t("messaging.email.held.openThread")}
            </Button>
          ) : null}
        </p>
      ) : (
        <p className="msg-hint">{t("messaging.email.held.noCustomer")}</p>
      )}
      {item.held_parts.length > 0 ? (
        <div className="msg-link-block" role="group" aria-label={t("messaging.email.filesAria")}>
          {item.held_parts.map((part) => (
            <HeldPartRow
              key={`${part.receipt_id}:${String(part.index)}`}
              part={part}
              locale={locale}
              waiting={false}
              canDownload
            />
          ))}
        </div>
      ) : null}
      {item.mailbox_count === 0 ? (
        <p className="msg-hint" data-testid="msg-held-no-mailbox">
          {t("messaging.email.held.noMailbox")}
        </p>
      ) : null}
      <div className="msg-toolbar">
        {item.mailbox_count > 0 ? (
          <Button
            size="sm"
            variant="primary"
            data-testid="msg-held-release"
            disabled={!online}
            onClick={() => {
              setConfirm("release");
            }}
          >
            {t("messaging.email.held.release")}
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="danger"
          data-testid="msg-held-discard"
          disabled={!online}
          onClick={() => {
            setConfirm("discard");
          }}
        >
          {t("messaging.email.held.discard")}
        </Button>
      </div>
      {confirm === "release" ? (
        <ReleaseConfirm
          item={item}
          online={online}
          onClose={() => {
            setConfirm(null);
          }}
          onDecidedElsewhere={onDecidedElsewhere}
        />
      ) : null}
      {confirm === "discard" ? (
        <DiscardConfirm
          item={item}
          online={online}
          onClose={() => {
            setConfirm(null);
          }}
          onDecidedElsewhere={onDecidedElsewhere}
        />
      ) : null}
    </article>
  );
}

/** Is this the refusal that says another person decided the mail first? */
function decidedByAnother(error: unknown): boolean {
  return error instanceof RequestError && error.code === "EMAIL_RECEIPT_NOT_HELD";
}

/**
 * Release's confirm (D-327 ruling 18). It says where the mail goes. For a
 * From with several mailboxes it asks the releaser to choose one, offered as
 * served (§7), and sends the chosen one's INDEX, never its value, so a
 * releaser without the address grant can choose and the request carries no
 * address. A choice left empty is marked here before anything is sent, and
 * a 422 EMAIL_RELEASE_MAILBOX_INVALID keeps the confirm open with the
 * chooser marked.
 */
function ReleaseConfirm({
  item,
  online,
  onClose,
  onDecidedElsewhere,
}: {
  item: EmailQuarantineItem;
  online: boolean;
  onClose: () => void;
  onDecidedElsewhere: () => void;
}) {
  const { t } = useTranslation();
  const release = useReleaseHeldMail();
  const [choice, setChoice] = useState("");
  const [invalid, setInvalid] = useState(false);
  const several = item.mailbox_count > 1;
  const submit = () => {
    if (several && choice === "") {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    release.mutate(
      { id: item.id, mailboxIndex: several ? Number(choice) : undefined },
      {
        onSuccess: onClose,
        onError: (error) => {
          if (decidedByAnother(error)) {
            onDecidedElsewhere();
            onClose();
          } else if (
            error instanceof RequestError &&
            error.code === "EMAIL_RELEASE_MAILBOX_INVALID"
          ) {
            setInvalid(true);
          }
        },
      },
    );
  };
  const mailboxRefused =
    release.error instanceof RequestError && release.error.code === "EMAIL_RELEASE_MAILBOX_INVALID";
  return (
    <Modal
      label={t("messaging.email.held.releaseTitle")}
      heading={t("messaging.email.held.releaseTitle")}
      closeLabel={t("messaging.common.close")}
      className="msg-modal"
      testId="msg-held-release-confirm"
      pending={release.isPending}
      onClose={onClose}
    >
      <div className="msg-link-block">
        <p className="msg-fact">{t("messaging.email.held.releaseBody")}</p>
        {several ? (
          <>
            <p className="msg-hint">{t("messaging.email.held.chooseMailboxHint")}</p>
            <Field label={t("messaging.email.held.chooseMailbox")} wide>
              <Select
                value={choice}
                aria-invalid={invalid ? "true" : undefined}
                data-testid="msg-held-mailbox"
                onChange={(e) => {
                  setChoice(e.target.value);
                  setInvalid(false);
                }}
              >
                <option value="" />
                {item.senders.map((sender, index) => (
                  <option key={`${String(index)}:${sender}`} value={String(index)}>
                    {sender}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        ) : null}
        {invalid ? (
          <p className="msg-error-note" role="alert" data-testid="msg-held-mailbox-invalid">
            {t("messaging.email.held.mailboxInvalid")}
          </p>
        ) : null}
        <div className="msg-toolbar">
          <Button size="sm" onClick={onClose}>
            {t("messaging.common.cancel")}
          </Button>
          <Button
            size="sm"
            variant="primary"
            data-testid="msg-held-release-confirm-btn"
            disabled={!online}
            busy={release.isPending}
            busyLabel={t("messaging.email.held.releasing")}
            onClick={submit}
          >
            {t("messaging.email.held.release")}
          </Button>
        </div>
        {release.isError && !mailboxRefused && !decidedByAnother(release.error) ? (
          <MutationFailureNote
            data-testid="msg-mutation-failed"
            text={t("messaging.email.held.releaseFailed")}
            error={release.error}
          />
        ) : null}
      </div>
    </Modal>
  );
}

/** Discard's confirm: the text cannot come back. */
function DiscardConfirm({
  item,
  online,
  onClose,
  onDecidedElsewhere,
}: {
  item: EmailQuarantineItem;
  online: boolean;
  onClose: () => void;
  onDecidedElsewhere: () => void;
}) {
  const { t } = useTranslation();
  const discard = useDiscardHeldMail();
  return (
    <Modal
      label={t("messaging.email.held.discardTitle")}
      heading={t("messaging.email.held.discardTitle")}
      closeLabel={t("messaging.common.close")}
      className="msg-modal"
      testId="msg-held-discard-confirm"
      pending={discard.isPending}
      onClose={onClose}
    >
      <div className="msg-link-block">
        <p className="msg-fact">{t("messaging.email.held.discardBody")}</p>
        <div className="msg-toolbar">
          <Button size="sm" onClick={onClose}>
            {t("messaging.common.cancel")}
          </Button>
          <Button
            size="sm"
            variant="danger"
            data-testid="msg-held-discard-confirm-btn"
            disabled={!online}
            busy={discard.isPending}
            busyLabel={t("messaging.email.held.discarding")}
            onClick={() => {
              discard.mutate(item.id, {
                onSuccess: onClose,
                onError: (error) => {
                  if (decidedByAnother(error)) {
                    onDecidedElsewhere();
                    onClose();
                  }
                },
              });
            }}
          >
            {t("messaging.email.held.discard")}
          </Button>
        </div>
        {discard.isError && !decidedByAnother(discard.error) ? (
          <MutationFailureNote
            data-testid="msg-mutation-failed"
            text={t("messaging.email.held.discardFailed")}
            error={discard.error}
          />
        ) : null}
      </div>
    </Modal>
  );
}
